// dashboard/auth/SetPasswordScreen.js
// Account activation + password reset, one screen. Reached from either an
// invite email or a forgot-password email — Supabase Auth fires the same
// PASSWORD_RECOVERY event for both, and App.js routes here either way. If no
// profiles row exists yet for this auth user (a brand new invite), also
// collects name and creates one; an existing user resetting their password
// just sets the new password. Extracted from App.js (2026-08-12 split).

import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { c, CAIRN_GLYPH_PATHS } from '../constants';
import { PRIVACY_POLICY_VERSION, TERMS_VERSION } from '../../legalDocumentVersions';

export default function SetPasswordScreen() {
  const { t } = useTranslation();
  const [checking,   setChecking]   = useState(true);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [firstName,  setFirstName]  = useState('');
  const [lastName,   setLastName]   = useState('');
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [session,    setSession]    = useState(null);
  const [legalAccepted, setLegalAccepted] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) { setChecking(false); return; }
      setSession(s);
      const { data: prof } = await supabase
        .from('profiles').select('id').eq('auth_user_id', s.user.id).maybeSingle();
      setIsNewProfile(!prof);
      setChecking(false);
    })();
  }, []);

  const canSubmit = password.length >= 8 && password === confirm
    && (!isNewProfile || (firstName.trim() && lastName.trim() && legalAccepted));

  // Wrap a Supabase auth call so a stalled request surfaces as an error
  // after 10s instead of leaving the button stuck on "Saving..." forever —
  // this client already had one known indefinite-hang class (see
  // supabase.js's navigator.locks bypass); this covers any other cause of
  // the same symptom without needing to pin down which await is stuck.
  function withTimeout(promise, label) {
    return Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error(t('setPassword.timedOut', { label }))), 10000)),
    ]);
  }

  const handleSubmit = async e => {
    e.preventDefault();
    if (!canSubmit || saving || !session) return;
    setSaving(true); setError('');
    try {
      if (isNewProfile) {
        const now = new Date().toISOString();
        const { error: pe } = await withTimeout(
          supabase.rpc('ensure_current_user_profile', {
            p_first_name: firstName.trim(),
            p_last_name: lastName.trim(),
            p_role: 'clinic',
            p_phone: null,
            p_anonymization_consent: false,
            p_anonymization_consent_at: null,
            p_tos_accepted_at: now,
            p_privacy_policy_accepted_at: now,
            p_tos_accepted_version: TERMS_VERSION,
            p_privacy_policy_accepted_version: PRIVACY_POLICY_VERSION,
            p_legal_acceptance_locale: document.documentElement.lang || 'en',
          }),
          t('setPassword.labelCreatingProfile'),
        );
        if (pe) throw pe;
      }

      // Creating the idempotent profile first means a password-call failure
      // can be retried safely. The old order could change the password and then
      // strand the account when its non-idempotent profile insert failed.
      const { error: ue } = await withTimeout(supabase.auth.updateUser({ password }), t('setPassword.labelSettingPassword'));
      if (ue) throw ue;

      // Brand-new-Doctor invitations (audit finding #2, 2026-09-10) — a
      // pending clinic_invites row (created via PermissionsView's add-
      // staff flow for an email with no existing account) is consumed
      // here, right after this account's profile exists to attach a real
      // clinic_members row to. A safe no-op for every other completion of
      // this screen (plain password resets, or a fresh signup that never
      // had a pending invite) — reason comes back 'no_pending_invite' and
      // nothing else happens.
      // Audit finding #9 (2026-09-10, second pass): previously only
      // checked for `reason === 'seat_unavailable'` and ignored the RPC's
      // own `error` entirely — a real database error, or the
      // `multi_clinic_not_supported`/`email_not_confirmed` reasons this
      // RPC can also return, silently fell through to the reload below,
      // landing the account on an unhelpful blank state with no
      // explanation. Every outcome now leads to a defined message; only
      // the two genuinely-safe-to-ignore no-ops (no invite existed at
      // all, or it was successfully consumed) fall through to the reload.
      const { data: consumeResult, error: consumeErr } = await supabase.rpc('consume_pending_clinic_invite');
      if (consumeErr) {
        console.error('consume_pending_clinic_invite failed:', consumeErr);
        setError(t('setPassword.errorInviteCheckFailed'));
        setSaving(false);
        return;
      }
      if (consumeResult?.consumed === false) {
        if (consumeResult.reason === 'seat_unavailable') {
          // The account itself is fully created and safe — only the
          // clinic membership couldn't activate (seats ran out between
          // the invite and this acceptance). Same message shape as
          // LoginScreen.js's existing pendingInvite/
          // DOCTOR_SEAT_LIMIT_REACHED handling for the existing-account
          // case.
          setError(t('setPassword.errorInviteSeatLimit'));
          setSaving(false);
          return;
        }
        if (consumeResult.reason === 'multi_clinic_not_supported') {
          setError(t('setPassword.errorInviteMultiClinic'));
          setSaving(false);
          return;
        }
        if (consumeResult.reason === 'email_not_confirmed') {
          setError(t('setPassword.errorInviteEmailNotConfirmed'));
          setSaving(false);
          return;
        }
        // 'no_pending_invite' / 'no_profile' are the expected no-op cases
        // (a plain password reset, or a signup that was never invited) —
        // fall through to the reload below exactly as before.
      }

      // Full reload is deliberate here — it re-runs the normal auth/profile/
      // clinic_members hydration from a clean slate instead of trying to
      // splice a brand-new profile into already-rendered app state.
      window.location.href = window.location.origin;
    } catch (err) {
      setError(err.message || t('setPassword.errorSave'));
      setSaving(false);
    }
  };

  if (checking) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: c.fog, fontSize: 13, color: c.textSoft, fontFamily: 'Inter, system-ui, sans-serif' }}>{t('common.loading')}</div>;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: c.fog, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ backgroundColor: c.white, borderRadius: 20, padding: 40, width: '100%', maxWidth: 380, boxShadow: '0 8px 32px rgba(79,100,115,0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <svg width="28" height="28" viewBox="0 0 128 128">
            {CAIRN_GLYPH_PATHS.map((d, i) => <path key={i} d={d} fill={c.slate} />)}
          </svg>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.slate, letterSpacing: 2 }}>Cairn</div>
            <div style={{ fontSize: 9, color: c.moss, letterSpacing: 2, textTransform: 'uppercase' }}>PET HEALTH</div>
          </div>
        </div>

        <h1 style={{ fontSize: 20, fontWeight: 600, color: c.slate, marginBottom: 4 }}>
          {isNewProfile ? t('setPassword.titleNewAccount') : t('setPassword.titleNewPassword')}
        </h1>
        <p style={{ fontSize: 13, color: c.textSoft, marginBottom: 24 }}>
          {isNewProfile
            ? t('setPassword.subtitleNewAccount')
            : t('setPassword.subtitleNewPassword')}
        </p>

        <form onSubmit={handleSubmit}>
          {isNewProfile && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-first-name" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 }}>{t('setPassword.firstName')}</label>
                <input id="signup-first-name" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} autoFocus
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 14, color: c.slate, boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-last-name" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 }}>{t('setPassword.lastName')}</label>
                <input id="signup-last-name" type="text" value={lastName} onChange={e => setLastName(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 14, color: c.slate, boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: c.textSoft, marginBottom: 14 }}>
                <input type="checkbox" checked={legalAccepted} onChange={e => setLegalAccepted(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  {t('setPassword.legalAcceptPrefix')}{' '}
                  <a href="https://cairnpethealth.com/terms" target="_blank" rel="noreferrer">{t('setPassword.terms')}</a>
                  {' '}{t('setPassword.legalAcceptMiddle')}{' '}
                  <a href="https://cairnpethealth.com/privacy" target="_blank" rel="noreferrer">{t('setPassword.privacy')}</a>.
                </span>
              </label>
            </>
          )}
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="signup-password" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 }}>{t('login.password')}</label>
            <input id="signup-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t('setPassword.passwordPlaceholder')}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 14, color: c.slate, boxSizing: 'border-box', outline: 'none' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="signup-confirm-password" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 }}>{t('setPassword.confirmPassword')}</label>
            <input id="signup-confirm-password" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t('setPassword.passwordPlaceholder')}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 14, color: c.slate, boxSizing: 'border-box', outline: 'none' }} />
            {password && confirm && password !== confirm && (
              <p style={{ fontSize: 11, color: c.error, marginTop: 5 }}>{t('setPassword.passwordMismatch')}</p>
            )}
            {password && password.length < 8 && (
              <p style={{ fontSize: 11, color: c.error, marginTop: 5 }}>{t('setPassword.passwordTooShort')}</p>
            )}
          </div>
          {error && <p style={{ fontSize: 12, color: c.error, marginBottom: 10 }}>{error}</p>}
          <button type="submit" disabled={!canSubmit || saving}
            style={{ width: '100%', padding: '12px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (!canSubmit || saving) ? 0.5 : 1, marginTop: 4 }}>
            {saving ? t('common.saving') : (isNewProfile ? t('setPassword.finishSetup') : t('setPassword.savePassword'))}
          </button>
        </form>
      </div>
    </div>
  );
}
